/**
 * Microsoft Clarity(세션 녹화) 시작 규칙 (2026-09-30).
 *
 * 고객사가 서비스를 어떻게 쓰는지 보려고 넣은 도구다. 옛 Clarity 계정은 잃어버려
 * 새 프로젝트로 다시 연결했다(아래 `CLARITY_PROJECT_ID`, 공개값 — 페이지 소스에 그대로 보인다).
 *
 * ## 🔴 내부 관리자 화면(/sysadmin)은 외부로 한 바이트도 나가면 안 된다
 *
 * "경로가 /sysadmin 이면 녹화를 멈춘다" 로는 막을 수 없다. clarity-js 소스(`core/history.ts`)를 보면:
 *  - `history.pushState` 를 가로채 **URL 이 바뀌면 stop → 250ms 뒤 새 URL 에서 자동 start** 한다.
 *    SPA 안에서 /sysadmin 으로 옮기면 그 화면을 새 페이지로 녹화한다.
 *  - `stop()` 은 멈추면서 쌓인 데이터를 **마지막으로 한 번 전송**한다(`upload(true)`).
 *  - 태그 스크립트는 CDN 에서 늘 최신본이 온다 — 이런 내부 동작에 기대는 차단은 언제 깨질지 모른다.
 *
 * 그래서 **`/sysadmin` 이 그려질 수 있는 페이지에는 Clarity 를 아예 싣지 않는다.**
 *  - /sysadmin 라우트는 `isSystemAdmin` 일 때만 등록된다(App.js) ⇒ 관리자 계정이면 로드하지 않는다.
 *  - 로그인·로그아웃은 모두 전체 페이지 이동(`window.location.href`)이라 한 페이지 안에서 계정이 바뀌지 않는다.
 *  - 주소창에 /sysadmin 을 직접 쳐서 들어온 경우도 로드하지 않는다(대소문자 무시 — react-router 도 무시한다).
 *
 * 부수 효과: 내부 직원이 고객 화면을 보는 것도 녹화되지 않는다 — 고객 이용 패턴만 보려는 목적에 오히려 맞다.
 */
import Clarity from '@microsoft/clarity';

export const CLARITY_PROJECT_ID = 'yqcyldm8sy';

/** Clarity 를 싣지 않는 경로 — /sysadmin 과 그 하위 전부. */
export const isClarityExcludedPath = (pathname) => {
  const p = String(pathname ?? '').toLowerCase();
  return p === '/sysadmin' || p.startsWith('/sysadmin/');
};

let started = false;

/** 이 페이지에서 Clarity 를 이미 실었는가. */
export const isClarityStarted = () => started;

/**
 * 조건이 맞을 때만 Clarity 를 한 번 싣는다. 실었으면 true.
 * 운영 빌드(`REACT_APP_ENV=prod`)가 아니면 아무것도 하지 않는다 — 로컬·개발 데이터가 섞이지 않게.
 */
export function startClarity({ isSystemAdmin, pathname }) {
  if (started) return false;
  if (process.env.REACT_APP_ENV !== 'prod') return false;
  if (isSystemAdmin === true) return false;
  if (isClarityExcludedPath(pathname)) return false;
  started = true;
  Clarity.init(CLARITY_PROJECT_ID);
  return true;
}
